/**
 * Warm theme preset – earthy, natural tones for wellness and integrative care.
 *
 * Designed for acupuncture, holistic medicine, and wellness practices where
 * warmth and trust are the primary signals. Key design decisions:
 * - Lora (body) + Playfair Display (heading) – serif warmth and approachability
 * - Earthy brown primary with sand accent – grounded, natural palette
 * - `cardStyle: filled` – soft filled backgrounds feel nurturing, not clinical
 * - `iconStyle: filled` – filled icons match the softer card treatment
 * - Wider sectionPadding (5rem) for an airy, unhurried layout
 *
 * AI-NOTE: This preset pairs with `heroStyle: image`. When generating pages
 * for wellness providers, hero images of nature, practitioners, and serene
 * environments work better than gradient overlays.
 */

import { ThemeSchema, type Theme } from '../types';
import { DEFAULT_DENSITY } from './base';

export const warmTheme: Theme = ThemeSchema.parse({
  name: 'warm',
  colors: {
    primary: '#735734',
    primaryDark: '#594125',
    primaryLight: '#A89075',
    // Accent darkened for AA contrast (the lighter #C4A777 is preserved as accentLight)
    accent: '#7D6537',
    accentDark: '#5C4924',
    accentLight: '#C4A777',
    background: '#FDFBF7',
    backgroundAlt: '#EDE8DA',
    surface: '#FFFFFF',
    text: '#3D3D3D',
    textMuted: '#595959',
    textInverse: '#FFFFFF',
    border: '#E8E2D9',
    // Status (darkened for AA non-text contrast)
    success: '#4F7B53',
    warning: '#8B6F2E',
    error: '#9F3F3B',
    // Medical-specific (warm-toned variants)
    evidenceStrong: '#4F7B53',
    evidenceModerate: '#8B6F2E',
    evidenceLow: '#5C5C5C',
    safetyWarning: '#9F3F3B',
    safetyInfo: '#3A5C7D',
  },
  typography: {
    fontFamily: '"Lora", Georgia, "Times New Roman", serif',
    fontFamilyHeading: '"Playfair Display", Georgia, serif',
    fontWeightHeading: '700',
    fontSizeBase: '1rem',
    fontSizeSm: '0.875rem',
    fontSizeLg: '1.125rem',
    fontSizeXl: '1.25rem',
    fontSize2xl: '1.5rem',
    fontSize3xl: '1.875rem',
    fontSize4xl: '2.25rem',
    lineHeightBase: '1.6',
    lineHeightTight: '1.3',
    lineHeightLoose: '1.8',
  },
  spacing: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
    '2xl': '3rem',
    '3xl': '4rem',
  },
  borderRadius: {
    none: '0',
    sm: '0.25rem',
    md: '0.5rem',
    lg: '0.75rem',
    xl: '1rem',
    full: '9999px',
  },
  shadows: {
    sm: '0 1px 3px 0 rgb(139 115 85 / 0.1)',
    md: '0 4px 6px -1px rgb(139 115 85 / 0.1), 0 2px 4px -2px rgb(139 115 85 / 0.1)',
    lg: '0 10px 15px -3px rgb(139 115 85 / 0.1), 0 4px 6px -4px rgb(139 115 85 / 0.1)',
    xl: '0 20px 25px -5px rgb(139 115 85 / 0.1), 0 8px 10px -6px rgb(139 115 85 / 0.1)',
  },
  layout: {
    heroStyle: 'image',
    cardStyle: 'filled',
    iconStyle: 'filled',
    buttonStyle: 'pill',
    sectionPadding: '5rem',
    contentMaxWidth: '60ch',
  },
  // HAL-495: shared layout rhythm + ch-based reading measures. See DEFAULT_DENSITY.
  density: DEFAULT_DENSITY,
});
